import { formatDateTime } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { InterviewType } from "@src/appV2/PlacementApplications/types/fetchInterviews.schema";
import { type Agent } from "@src/appV2/User/api/useSearchAgents";
import { parseISO } from "date-fns";
import { isValidPhoneNumber } from "libphonenumber-js";
import { z } from "zod";

import { type PlacementWithDetails } from "../api/useFetchPaginatedPlacements";
import { InterviewStatus } from "./fetchPlacements.schema";

export const assignCandidateSchema = z.object({
  worker: z.custom<Agent>().refine((value) => isDefined(value), "Please select a worker"),
  availability: z
    .object({
      start: z.string(),
      end: z.string(),
    })
    .optional(),
  interviewType: z.nativeEnum(InterviewType, {
    errorMap: () => ({ message: "Please select an Interview Type" }),
  }),
  bypassLicenseCheck: z.boolean(),
  bypassPlacementDocumentRequirementCheck: z.boolean(),
});

export type AssignCandidateFormData = z.infer<typeof assignCandidateSchema>;

export function hasAppliedToPlacement(placement?: PlacementWithDetails) {
  return (placement?.applications?.length ?? 0) > 0;
}

// Might have to also check for rescheduled interviews
export function hasBookedInterview(placement?: PlacementWithDetails) {
  const hasAtLeastOneApplication = (placement?.applications?.length ?? 0) > 0;
  const isInterviewBooked =
    placement?.interviews?.some(
      (interview) => interview.attributes.status === InterviewStatus.BOOKED
    ) ?? false;
  return hasAtLeastOneApplication && isInterviewBooked;
}

export function getPastInterview(
  timeZone: string,
  placement?: PlacementWithDetails,
  interviewType?: InterviewType
) {
  if (!placement) {
    return undefined;
  }

  const bookedInterview = placement.interviews.find(
    (interview) =>
      interview.attributes.status === InterviewStatus.BOOKED &&
      (!interviewType || interview.attributes.type === interviewType)
  );

  if (bookedInterview) {
    return {
      text: `Interview booked for ${formatDateTime(parseISO(bookedInterview.attributes.start), {
        timeZone,
      })}`,
      color: "success" as const,
    };
  }

  const confirmedInterview = placement.interviews.find(
    (interview) =>
      interview.attributes.status === InterviewStatus.ATTENDANCE_CONFIRMED &&
      (!interviewType || interview.attributes.type === interviewType)
  );

  if (confirmedInterview) {
    return {
      text: `Interview attended on ${formatDateTime(parseISO(confirmedInterview.attributes.start), {
        timeZone,
      })}`,
      color: "primary" as const,
    };
  }

  return undefined;
}

export function isMongoObjectId(value: string): boolean {
  return /^[\dA-Fa-f]{24}$/.test(value);
}

export function getSearchFilter(searchInput: string) {
  if (!searchInput) {
    return {};
  }

  // Check if input is a MongoDB ObjectId
  if (isMongoObjectId(searchInput)) {
    return { "worker.id": searchInput };
  }

  // Check if input is a valid phone number (simple pattern matching)
  // This supports formats like: 1234567890, 123-456-7890, (123) 456-7890
  if (isValidPhoneNumber(searchInput, "US")) {
    // Strip all non-numeric characters from the phone number
    const numericOnlyPhone = searchInput.replaceAll(/\D/g, "");
    return { "worker.phoneNumber": numericOnlyPhone };
  }

  // Default to name search
  return { "worker.name": searchInput };
}
